import React from 'react';
import { Container, Row, Col, Card, Badge, Breadcrumb, ProgressBar } from 'react-bootstrap';
import SEOHead from '../components/SEOHead';
import { testimonials } from '../data/businessData';

const ReviewsPage: React.FC = () => {
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span key={index} className={index < rating ? 'text-warning' : 'text-muted'}>
        ★
      </span>
    ));
  };

  const ratingBreakdown = [
    { stars: 5, count: 400, percentage: 80 },
    { stars: 4, count: 80, percentage: 16 },
    { stars: 3, count: 15, percentage: 3 },
    { stars: 2, count: 3, percentage: 0.6 },
    { stars: 1, count: 2, percentage: 0.4 }
  ];

  const serviceReviews = [
    { service: 'Car Denting & Painting', rating: 4.9, reviews: 150 },
    { service: 'Car Wash Service', rating: 4.8, reviews: 200 },
    { service: 'AC Repair Service', rating: 4.7, reviews: 80 },
    { service: 'Mechanical Repairs', rating: 4.8, reviews: 120 },
    { service: 'Regular Service', rating: 4.9, reviews: 180 }
  ];

  return (
    <>
      <SEOHead page="testimonials" />
      
      {/* Breadcrumb */}
      <Container className="py-3">
        <Breadcrumb>
          <Breadcrumb.Item href="#home">Home</Breadcrumb.Item>
          <Breadcrumb.Item active>Customer Reviews</Breadcrumb.Item>
        </Breadcrumb>
      </Container>

      {/* Reviews Header */}
      <section className="py-5 bg-primary text-white">
        <Container>
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-3">Customer Reviews & Testimonials</h1>
            <p className="lead mb-0">
              See what our satisfied customers say about Laxmi Tyre & Motors car service
            </p>
          </div>
        </Container>
      </section>

      {/* Overall Rating Summary */}
      <section className="py-5">
        <Container>
          <Row>
            <Col lg={4} className="mb-4">
              <Card className="border-0 shadow-sm text-center h-100">
                <Card.Body>
                  <div className="display-3 fw-bold text-primary mb-2">4.8</div>
                  <div className="mb-3">
                    {renderStars(5)}
                  </div>
                  <h5 className="fw-bold mb-2">Excellent Rating</h5>
                  <p className="text-muted mb-0">Based on 500+ customer reviews</p>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4} className="mb-4">
              <Card className="border-0 shadow-sm text-center h-100">
                <Card.Body>
                  <div className="display-3 fw-bold text-success mb-2">98%</div>
                  <h5 className="fw-bold mb-2">Satisfaction Rate</h5>
                  <p className="text-muted mb-0">Customers would recommend us to others</p>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4} className="mb-4">
              <Card className="border-0 shadow-sm text-center h-100">
                <Card.Body>
                  <div className="display-3 fw-bold text-warning mb-2">5000+</div>
                  <h5 className="fw-bold mb-2">Happy Customers</h5>
                  <p className="text-muted mb-0">Served since 2011 in Indore</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Rating Breakdown */}
      <section className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="h3 fw-bold text-dark mb-3">Rating Breakdown</h2>
            <p className="text-muted">
              Detailed analysis of customer ratings for our car services
            </p>
          </div>

          <Row>
            <Col lg={6} className="mb-4">
              <Card className="border-0 shadow-sm">
                <Card.Header>
                  <h5 className="mb-0 fw-bold">Overall Rating Distribution</h5>
                </Card.Header>
                <Card.Body>
                  {ratingBreakdown.map((item, index) => (
                    <div key={index} className="d-flex align-items-center mb-3">
                      <div className="me-3" style={{ minWidth: '60px' }}>
                        <span className="fw-bold">{item.stars} ★</span>
                      </div>
                      <div className="flex-grow-1 me-3">
                        <ProgressBar 
                          now={item.percentage} 
                          variant={item.stars >= 4 ? 'success' : item.stars >= 3 ? 'warning' : 'danger'}
                          style={{ height: '8px' }}
                        />
                      </div>
                      <div style={{ minWidth: '80px' }}>
                        <small className="text-muted">{item.count} reviews</small>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-4">
              <Card className="border-0 shadow-sm">
                <Card.Header>
                  <h5 className="mb-0 fw-bold">Service-wise Ratings</h5>
                </Card.Header>
                <Card.Body>
                  {serviceReviews.map((item, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                      <div>
                        <h6 className="mb-1">{item.service}</h6>
                        <div className="d-flex align-items-center">
                          <span className="text-warning me-2">{renderStars(Math.round(item.rating))}</span>
                          <span className="fw-bold text-primary">{item.rating}</span>
                        </div>
                      </div>
                      <div className="text-end">
                        <small className="text-muted">{item.reviews} reviews</small>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Customer Testimonials */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="h3 fw-bold text-dark mb-3">What Our Customers Say</h2>
            <p className="text-muted">
              Real feedback from real customers about our car service quality
            </p>
          </div>

          <Row>
            {testimonials.map((testimonial) => (
              <Col lg={6} className="mb-4" key={testimonial.id}>
                <Card className="h-100 border-0 shadow-sm testimonial-card">
                  <Card.Body className="d-flex flex-column">
                    <div className="mb-3">
                      <div className="text-primary display-6 opacity-25">💬</div>
                    </div>
                    
                    <blockquote className="blockquote flex-grow-1 mb-4">
                      <p className="mb-0 text-muted">"{testimonial.comment}"</p>
                    </blockquote>
                    
                    <div className="mt-auto">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6 className="fw-bold mb-1">{testimonial.name}</h6>
                          <Badge bg="primary" className="small">
                            {testimonial.service}
                          </Badge>
                        </div>
                        <div className="text-end">
                          <div className="mb-1">
                            {renderStars(testimonial.rating)}
                          </div>
                          <small className="text-muted">{testimonial.rating}/5 stars</small>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Additional Reviews */}
          <Row className="mt-4">
            <Col lg={6} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="mb-3">
                    <div className="text-primary display-6 opacity-25">💬</div>
                  </div>
                  <blockquote className="blockquote mb-4">
                    <p className="mb-0 text-muted">
                      "Excellent service for my Maruti Swift. The denting work was flawless and 
                      the painting matched perfectly. Very professional team and reasonable pricing."
                    </p>
                  </blockquote>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="fw-bold mb-1">Deepak Sharma</h6>
                      <Badge bg="primary" className="small">Car Denting & Painting</Badge>
                    </div>
                    <div className="text-end">
                      <div className="mb-1">{renderStars(5)}</div>
                      <small className="text-muted">5/5 stars</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <div className="mb-3">
                    <div className="text-primary display-6 opacity-25">💬</div>
                  </div>
                  <blockquote className="blockquote mb-4">
                    <p className="mb-0 text-muted">
                      "Been getting my Honda City serviced here for 2 years. Always satisfied 
                      with their work quality and honest pricing. Highly recommend for regular maintenance."
                    </p>
                  </blockquote>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="fw-bold mb-1">Meera Agarwal</h6>
                      <Badge bg="primary" className="small">Regular Service</Badge>
                    </div>
                    <div className="text-end">
                      <div className="mb-1">{renderStars(5)}</div>
                      <small className="text-muted">5/5 stars</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Review Highlights */}
      <section className="py-5 bg-dark text-white">
        <Container>
          <div className="text-center mb-5">
            <h2 className="h3 fw-bold mb-3">Why Customers Choose Us</h2>
            <p className="text-light">
              Key highlights from customer feedback and reviews
            </p>
          </div>

          <Row>
            <Col md={3} className="mb-4 text-center">
              <div className="display-6 text-success mb-3">👨‍🔧</div>
              <h5 className="fw-bold">Expert Technicians</h5>
              <p className="mb-0">Skilled professionals with years of experience</p>
            </Col>
            <Col md={3} className="mb-4 text-center">
              <div className="display-6 text-warning mb-3">💰</div>
              <h5 className="fw-bold">Fair Pricing</h5>
              <p className="mb-0">Transparent and competitive pricing</p>
            </Col>
            <Col md={3} className="mb-4 text-center">
              <div className="display-6 text-info mb-3">⚡</div>
              <h5 className="fw-bold">Quick Service</h5>
              <p className="mb-0">Fast turnaround without compromising quality</p>
            </Col>
            <Col md={3} className="mb-4 text-center">
              <div className="display-6 text-primary mb-3">🏆</div>
              <h5 className="fw-bold">Quality Work</h5>
              <p className="mb-0">High-quality repairs and maintenance</p>
            </Col>
          </Row>
        </Container>
      </section>

      <style>{`
        .testimonial-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .testimonial-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
        }
      `}</style>
    </>
  );
};

export default ReviewsPage;